import { addSpanEvent, ImprHeaders, type Replacement, updateUrl } from '@imprivata-cloud/common';
import { CodingContext, getRSAPublicKey } from '@imprivata-cloud/dataprivacy';
import { ENVIRONMENT, TldToEnv } from '../featureFlagsUtils';
import {
  AMPortalService,
  ClientHeader,
  CommonHeadersJson,
  type Endpoints,
  type Entitlement,
  SearchParams,
  SystemType,
} from './constants';
import type { TenantInfoResponse } from './types';

export interface RequestInitImpr extends RequestInit {
  workflowId?: string | null;
  systemType?: SystemType;
}

/// Shared fetch wrapper
export const fetcher = async (
  endpoint: (typeof Endpoints)[keyof typeof Endpoints] | string,
  { workflowId, systemType, ...options }: RequestInitImpr,
  tenantIdParam?: string,
) => {
  const normalizedTenantId = tenantIdParam?.trim() || undefined;
  const tenantId =
    normalizedTenantId ??
    getTenantIdFromUrl(globalThis.location.hostname)?.tenantId ??
    new URLSearchParams(globalThis.location.search).get(SearchParams.TENANT_ID);
  const apiUrl = getApiUrl(tenantId);
  addSpanEvent(`fetching ${endpoint}`);

  return fetch(endpoint.startsWith('http') ? endpoint : `${apiUrl}${endpoint}`, {
    credentials: 'include',
    ...options,
    headers: {
      ...CommonHeadersJson,
      ...(workflowId ? { 'Impr-Workflow-Id': workflowId } : {}),
      ...(systemType === SystemType.SETUP || endpoint.includes(AMPortalService.AM_PORTAL_SETUP)
        ? { [ImprHeaders.ImprClientName]: btoa(JSON.stringify({ name: ClientHeader.SETUP })) }
        : {}),
      ...(options.headers || {}),
    },
  })
    .then(async response => {
      if (response.ok) {
        return response
          .json()
          .then(json => json)
          .catch(() => null); //response with empty body
      }
      const body = await response.json().catch(() => null);
      addSpanEvent(`error fetching ${endpoint}`, { error: body });
      return Promise.reject({ ...body?.error, httpStatus: response.status });
    })
    .catch(error => {
      addSpanEvent(`error fetching ${endpoint}`, { error });
      console.error('Error fetching data:', endpoint, error);
      return Promise.reject(error);
    });
};

export const fetcherRaw = async (
  endpoint: string,
  options: RequestInitImpr,
  tenantIdParam?: string,
) => {
  const normalizedTenantId = tenantIdParam?.trim() || undefined;
  const tenantId =
    normalizedTenantId ??
    getTenantIdFromUrl(globalThis.location.hostname)?.tenantId ??
    new URLSearchParams(globalThis.location.search).get(SearchParams.TENANT_ID);
  const apiUrl = getApiUrl(tenantId);
  addSpanEvent(`fetching ${endpoint}`);

  const { headers: customHeaders, ...restOptions } = options;

  const response = await fetch(endpoint.startsWith('http') ? endpoint : `${apiUrl}${endpoint}`, {
    credentials: 'include',
    ...restOptions,
    headers: { ...(customHeaders || {}) },
  });

  if (!response.ok) {
    // The report endpoints stream CSV, so a failure can come from an intermediary as HTML or an
    // empty body instead of ErrorResponseBody. Parsing blind threw a SyntaxError that replaced the
    // real status, hiding 403s and gateway timeouts behind a generic system error.
    const body = await response.text().catch(() => '');
    const parsed = parseJsonBody(body);
    const error = {
      ...(parsed ?? {
        code: `http-${response.status}`,
        message: response.statusText || body.slice(0, 500),
      }),
      status: response.status,
    };
    addSpanEvent(`error fetching ${endpoint}`, {
      status: response.status,
      error: JSON.stringify(error),
    });
    return Promise.reject(error);
  }
  return response;
};

/** Narrows a `fetcherRaw` rejection to one that carries the HTTP status it failed with. */
export function isHttpFailure(error: unknown): error is { status: number } {
  return (
    typeof error === 'object'
    && error !== null
    && 'status' in error
    && typeof (error as { status: unknown }).status === 'number'
  );
}

function parseJsonBody(body: string): unknown {
  if (!body) {
    return undefined;
  }
  try {
    const json: unknown = JSON.parse(body);
    if (typeof json !== 'object' || json === null) {
      return undefined;
    }
    return 'error' in json ? json.error : json;
  } catch {
    return undefined;
  }
}

// Interpolate the API URL based on the current environment
export function getApiUrl(tenantId?: string | null): string {
  const host = tenantId ? tenantId : 'astra';
  const urls = _getUrl({ apiUrl: { sub: `${host}.sys` } });
  return urls?.apiUrl;
}

// Get the current environment and top-level domain from the current host
export function _getUrl(urlToBeGenerated: Record<string, Replacement>) {
  const url = getCurrentHostname();

  if (url?.includes('localhost')) {
    return {
      apiUrl: 'http://localhost:8080',
      metadataUrl: 'https://metadata.app.dev.imprivata.cloud',
    };
  }

  let hostname = url;
  //Get tenantId if present
  const domainInfo = getTenantIdFromUrl(url);

  if (domainInfo) {
    hostname = domainInfo.urlWithoutTenantId;
  }

  const customRegex = /^(?:https?:\/\/)?([A-Za-z0-9_]+)\w+/;
  const urlRegex = hostname?.includes('.app') ? undefined : customRegex;
  return updateUrl(hostname, urlToBeGenerated, 'https://', urlRegex);
}

/**
 * Checks if a hostname contains a tenant ID
 * @param hostname The hostname to check
 * @returns Boolean indicating if tenant ID is present
 */
export function getTenantIdFromUrl(
  hostname: string,
): { tenantId: string; urlWithoutTenantId: string } | null {
  if (!hostname) {
    return null;
  }

  // Split the hostname into parts
  const parts = hostname.split('.');

  // Identify common domain patterns
  const domainParts = 2; // e.g., imprivata.com, imprivata.cloud, imprivata.eu
  const envPart = parts.some(part => /^(dev|int|stg|eph|prod)(\d+)?$/.test(part)) ? 1 : 0;
  const appPart = parts.some(part => part === 'app') ? 1 : 0;

  // Standard URL has: service + optional app + optional env + domain
  // e.g., access.app.dev.imprivata.cloud has 1+1+1+2=5 parts
  const standardParts = 1 + appPart + envPart + domainParts;

  // If we have more parts than the standard pattern, assume the first is a tenant ID
  if (parts.length > standardParts) {
    const tenantIdPart = parts[0];
    const urlWithoutTenantId = parts.slice(1).join('.');
    return { tenantId: tenantIdPart, urlWithoutTenantId };
  }

  return null;
}

export async function createCodingCtxV2(): Promise<CodingContext> {
  const { metadataUrl } = _getUrl({ metadataUrl: { sub: 'metadata.app' } });
  const rsaPublicKey = await getRSAPublicKey(`${metadataUrl}/data-privacy/public.json`);
  return CodingContext.createContextV2(rsaPublicKey.key, rsaPublicKey.version);
}

export function _getEnv(): ENVIRONMENT {
  const url = getCurrentHostname();
  const match = url.match(/\b(int|dev|stg)\b/);
  if (match) {
    return ENVIRONMENT[match[1] as keyof typeof ENVIRONMENT];
  }
  if (url.includes('localhost')) {
    return ENVIRONMENT.local;
  }
  // Check for TLD
  const tldMatch = url.match(/\.([a-z]+)$/i);
  const tld = tldMatch ? tldMatch[1] : null;
  return tld ? (TldToEnv[tld] ?? ENVIRONMENT.prod) : ENVIRONMENT.prod;
}

function getCurrentHostname(): string {
  const { hostname, host } = window.location;
  if (hostname) { return hostname; }
  return host.split(':')[0];
}

export function hasEntitlement(
  tenantInfo: TenantInfoResponse | undefined,
  entitlement: Entitlement,
): boolean {
  return tenantInfo?.currentTenant?.entitlements?.includes(entitlement) ?? false;
}
